import { MedicalTypeBaseUrlMap } from "@qendo/medical";
import { type MedicalType, MedicalTypeNameMap } from "@qendo/medical/types";
import { router } from "expo-router";
import { useMemo, useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { ConfirmationModal } from "~/components/layout/modal/ConfirmationModal";
import { Spacer } from "~/components/layout/Spacer";
import { Only } from "~/components/permission/Only";
import { Items, Padding } from "~/constants/layout";
import { useMedical } from "~/providers/MedicalProvider";

export type DeleteMedicalButtonProps<T extends MedicalType> = {
    readonly type: T;
    readonly id: string;
};

export const DeleteMedicalButton = <T extends MedicalType>({ type, id }: DeleteMedicalButtonProps<T>) => {
    const { findMedical, canUpdateMedical, destroyMedical } = useMedical();
    const [confirm, setConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const item = useMemo(() => findMedical(type, id), [findMedical, id, type]);
    const name = MedicalTypeNameMap[type];
    const canDelete = useMemo(() => Boolean(canUpdateMedical(type) && item), [canUpdateMedical, item, type]);
    const doDelete = async () => {
        if (!canDelete || deleting) {
            return;
        }

        setDeleting(true);

        const handleDismiss = () => {
            router.dismissTo(MedicalTypeBaseUrlMap[type]);
        };

        await destroyMedical(type, id)
            .then(handleDismiss)
            .finally(() => setDeleting(false));
    };

    return (
        <Only when={canDelete}>
            <Spacer />
            <View style={[Items.center, Padding.bottom_md]}>
                <Button
                    icon="trash"
                    title={`Delete ${name}`}
                    variant="destructive"
                    size="lg"
                    onPress={() => setConfirm(true)}
                />
            </View>
            {confirm && (
                <ConfirmationModal
                    onConfirm={doDelete}
                    title={`Are you sure you want to delete this ${name}?`}
                    confirmText={`Delete ${name}`}
                    confirmVariant="destructive-quiet"
                    loading={deleting}
                    onRequestClose={() => setConfirm(false)}
                />
            )}
        </Only>
    );
};
